import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mountain } from 'lucide-react';
import { GlassCard } from '@/components/shared/GlassCard';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { Button } from '@/components/ui/button';
import { Pulse } from '@/components/shared/Pulse';
import { useTheme } from '@/hooks/useTheme';
import { brand } from '@/lib/theme/tokens';
import { unsplash } from '@/lib/utils/img';
import { formatRupees } from '@/lib/utils/format';
import { cn } from '@/lib/utils/cn';
import { DEFAULT_REGION, REGIONS, type Region } from '../data/regions';
import { useListings } from '@/features/marketplace/hooks/useListings';

export function KashmirMap() {
  const navigate = useNavigate();
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const [activeKey, setActiveKey] = useState<Region['key']>(DEFAULT_REGION);
  const active = REGIONS.find((r) => r.key === activeKey) ?? REGIONS[0]!;

  const { data: regionActivities = [] } = useListings({
    type: 'activity',
    city: active.name,
    per_page: 3,
  });

  return (
    <section className="mx-auto mt-24 max-w-7xl px-4 md:px-6">
      <SectionHeader
        align="center"
        kicker="The valley · interactive"
        title={
          <>
            Six worlds.{' '}
            <em className="text-[var(--color-teal)]">One short flight away.</em>
          </>
        }
        description="Tap a region to see what's bookable this month — season-aware, weather-aware, real-time."
      />

      <GlassCard className="mt-10 grid grid-cols-1 overflow-hidden p-0 lg:grid-cols-[1fr_380px] lg:h-[620px]">
        {/* Map canvas */}
        <div
          className="relative border-b border-[var(--glass-edge)] lg:border-b-0 lg:border-r"
          style={{
            background: isDark
              ? `radial-gradient(ellipse at 30% 30%, rgba(46,170,138,0.14), transparent 60%), rgba(255,255,255,0.02)`
              : `radial-gradient(ellipse at 30% 30%, rgba(46,170,138,0.18), transparent 60%), rgba(255,255,255,0.3)`,
            aspectRatio: '4 / 3',
            minHeight: 0,
          }}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="xMidYMid meet"
            className="absolute inset-0 h-full w-full"
            role="img"
            aria-label="Map of the Kashmir Valley with six interactive region pins"
          >
            <defs>
              <linearGradient id="uoKashmirFill" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={brand.teal} stopOpacity="0.18" />
                <stop offset="1" stopColor={brand.saffron} stopOpacity="0.10" />
              </linearGradient>
              <pattern id="uoTopo" width="6" height="6" patternUnits="userSpaceOnUse">
                <path
                  d="M0 3 Q1.5 1.5 3 3 T6 3"
                  fill="none"
                  stroke={isDark ? '#3a4a60' : '#a8b8c8'}
                  strokeWidth="0.18"
                  opacity="0.5"
                />
              </pattern>
            </defs>

            {/* Valley silhouette */}
            <path
              d="M 12 50 C 8 42, 10 30, 18 24 C 22 18, 30 14, 38 14 C 46 12, 55 12, 62 14 C 68 12, 73 10, 78 10 C 83 12, 86 18, 84 24 C 90 28, 92 36, 88 42 C 86 50, 80 56, 74 58 C 70 64, 62 70, 56 70 C 50 76, 42 80, 34 76 C 26 74, 20 68, 18 60 C 14 56, 12 52, 12 50 Z"
              fill="url(#uoKashmirFill)"
              stroke={brand.teal}
              strokeWidth="0.4"
              strokeOpacity="0.55"
            />
            <path
              d="M 12 50 C 8 42, 10 30, 18 24 C 22 18, 30 14, 38 14 C 46 12, 55 12, 62 14 C 68 12, 73 10, 78 10 C 83 12, 86 18, 84 24 C 90 28, 92 36, 88 42 C 86 50, 80 56, 74 58 C 70 64, 62 70, 56 70 C 50 76, 42 80, 34 76 C 26 74, 20 68, 18 60 C 14 56, 12 52, 12 50 Z"
              fill="url(#uoTopo)"
            />

            {/* Mountain ranges — dashed contours */}
            <path
              d="M 14 60 Q 28 72 50 74 Q 64 72 72 64"
              fill="none"
              stroke={isDark ? '#5a6a80' : '#8a9aa8'}
              strokeWidth="0.3"
              strokeDasharray="0.6 0.4"
              opacity="0.8"
            />
            <path
              d="M 18 22 Q 35 16 56 16 Q 70 16 82 18"
              fill="none"
              stroke={isDark ? '#5a6a80' : '#8a9aa8'}
              strokeWidth="0.3"
              strokeDasharray="0.6 0.4"
              opacity="0.8"
            />

            {/* River + lakes */}
            <path
              d="M 18 50 Q 28 48 38 50 Q 48 52 56 48 Q 64 44 74 42"
              fill="none"
              stroke={isDark ? '#4a8aa8' : '#5a9ec0'}
              strokeWidth="0.6"
              opacity="0.7"
            />
            <ellipse cx="40" cy="46" rx="3.2" ry="2" fill={isDark ? '#3a6e8c' : '#7aaec5'} opacity="0.55" />
            <ellipse cx="48" cy="36" rx="2.4" ry="1.4" fill={isDark ? '#3a6e8c' : '#7aaec5'} opacity="0.55" />

            {/* Labels */}
            <text
              x="50"
              y="84"
              textAnchor="middle"
              fontFamily="Playfair Display, serif"
              fontSize="3.2"
              fontWeight="600"
              letterSpacing="0.6"
              fill={isDark ? 'rgba(255,255,255,0.10)' : 'rgba(21,17,13,0.10)'}
            >
              KASHMIR VALLEY
            </text>
            <text
              x="50"
              y="88"
              textAnchor="middle"
              fontFamily="DM Sans, sans-serif"
              fontSize="1.4"
              letterSpacing="0.4"
              fill={isDark ? 'rgba(255,255,255,0.18)' : 'rgba(21,17,13,0.22)'}
            >
              JAMMU &amp; KASHMIR · INDIA
            </text>
            <text
              x="18"
              y="76"
              fontFamily="DM Sans, sans-serif"
              fontSize="1.4"
              fontStyle="italic"
              fill={isDark ? 'rgba(255,255,255,0.32)' : 'rgba(21,17,13,0.4)'}
            >
              Pir Panjal Range
            </text>
            <text
              x="62"
              y="20"
              fontFamily="DM Sans, sans-serif"
              fontSize="1.4"
              fontStyle="italic"
              fill={isDark ? 'rgba(255,255,255,0.32)' : 'rgba(21,17,13,0.4)'}
            >
              Greater Himalaya
            </text>
            <text
              x="44"
              y="46"
              fontFamily="DM Sans, sans-serif"
              fontSize="1.3"
              fill={isDark ? 'rgba(255,255,255,0.42)' : 'rgba(21,17,13,0.5)'}
            >
              Dal
            </text>

            {/* North compass + scale */}
            <g transform="translate(91, 86)">
              <circle
                r="3.5"
                fill="none"
                stroke={isDark ? 'rgba(255,255,255,0.25)' : 'rgba(21,17,13,0.25)'}
                strokeWidth="0.2"
              />
              <path d="M0 -3.2 L0.8 0 L0 3.2 L-0.8 0 Z" fill={brand.teal} />
              <text
                textAnchor="middle"
                y="-4"
                fontFamily="DM Sans"
                fontSize="1.4"
                fontWeight="700"
                fill={isDark ? 'rgba(255,255,255,0.6)' : 'rgba(21,17,13,0.6)'}
              >
                N
              </text>
            </g>
            <g transform="translate(6, 90)">
              <line x1="0" y1="0" x2="14" y2="0" stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(21,17,13,0.5)'} strokeWidth="0.2" />
              <line x1="0" y1="-0.6" x2="0" y2="0.6" stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(21,17,13,0.5)'} strokeWidth="0.2" />
              <line x1="14" y1="-0.6" x2="14" y2="0.6" stroke={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(21,17,13,0.5)'} strokeWidth="0.2" />
              <text x="7" y="2.4" textAnchor="middle" fontFamily="DM Sans" fontSize="1.1" fill={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(21,17,13,0.55)'}>
                50 km
              </text>
            </g>
          </svg>

          {/* Pin buttons (HTML on top of SVG so they're tappable + keyboard-focusable) */}
          {REGIONS.map((p) => {
            const isActive = p.key === activeKey;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setActiveKey(p.key)}
                aria-label={`${p.name} — ${p.sub}`}
                aria-pressed={isActive}
                className="absolute -translate-x-1/2 -translate-y-full cursor-pointer border-0 bg-transparent p-0"
                style={{ left: `${p.x}%`, top: `${p.y}%`, zIndex: 2 }}
              >
                <span className="flex flex-col items-center">
                  <span
                    className={cn(
                      'whitespace-nowrap rounded-lg border px-2.5 py-1 text-[11px] font-semibold backdrop-blur-md transition-all',
                      isActive
                        ? 'text-white shadow-[0_0_24px_rgba(0,0,0,0.5)]'
                        : 'text-ink',
                    )}
                    style={
                      isActive
                        ? {
                            background: p.color,
                            borderColor: p.color,
                            boxShadow: `0 0 28px ${p.color}90`,
                          }
                        : {
                            background: isDark ? 'rgba(20,24,32,0.85)' : 'rgba(255,255,255,0.9)',
                            borderColor: 'var(--glass-edge)',
                          }
                    }
                  >
                    {p.name}
                    {isActive && (
                      <span className="ml-1.5 font-normal opacity-85">· {p.count}</span>
                    )}
                  </span>
                  <span
                    className="mt-0.5 rounded-full transition-all"
                    style={{
                      width: isActive ? 14 : 8,
                      height: isActive ? 14 : 8,
                      background: p.color,
                      boxShadow: isActive
                        ? `0 0 0 4px ${p.color}30, 0 0 0 10px ${p.color}12`
                        : `0 0 0 3px ${p.color}30`,
                    }}
                  />
                </span>
              </button>
            );
          })}

          {/* "Live" indicator overlay */}
          <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-xl border border-[var(--glass-edge)] glass px-3 py-2 text-[11px]">
            <Pulse color={brand.ok} size={8} />
            <span>
              <span className="font-semibold text-ink">May 2026</span>
              <span className="ml-1.5 text-ink-dim">Spring · Trek opening</span>
            </span>
          </div>
        </div>

        {/* Side panel */}
        <aside
          className="flex flex-col gap-3.5 overflow-y-auto p-6"
          style={{
            background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.4)',
          }}
        >
          <div>
            <div
              className="text-[11px] font-bold uppercase tracking-[0.2em]"
              style={{ color: active.color }}
            >
              {active.season}
            </div>
            <h3 className="font-display mt-1 text-3xl font-semibold leading-tight tracking-tight text-ink">
              {active.name}
            </h3>
            <div className="text-[13px] text-ink-dim">
              {active.sub} · {active.count} experiences
            </div>
          </div>

          <div className="overflow-hidden rounded-xl">
            <img
              src={unsplash(active.image, { w: 800 })}
              alt={active.name}
              className="aspect-[16/9] w-full object-cover"
            />
          </div>

          <div className="flex flex-col gap-2.5">
            {regionActivities.length === 0 ? (
              <>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 rounded-xl border border-[var(--glass-edge)] p-2.5 animate-pulse"
                  >
                    <span className="h-9 w-9 shrink-0 rounded-[10px] bg-[var(--glass-hi)]" />
                    <span className="min-w-0 flex-1 flex flex-col gap-1.5">
                      <span className="h-3 w-3/4 rounded bg-[var(--glass-hi)]" />
                      <span className="h-2.5 w-1/2 rounded bg-[var(--glass-hi)]" />
                    </span>
                    <span className="flex flex-col items-end gap-1.5">
                      <span className="h-3 w-12 rounded bg-[var(--glass-hi)]" />
                      <span className="h-2.5 w-10 rounded bg-[var(--glass-hi)]" />
                    </span>
                  </div>
                ))}
              </>
            ) : (
              regionActivities.map((exp) => {
                const price = Math.round((exp.price_paisa ?? 0) / 100);
                return (
                  <button
                    key={exp.id}
                    type="button"
                    onClick={() => navigate('/search')}
                    className="flex items-center gap-3 rounded-xl border border-[var(--glass-edge)] glass p-2.5 text-left transition-colors hover:bg-[var(--glass-hi)]"
                  >
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]"
                      style={{ background: `${active.color}24` }}
                    >
                      <Mountain size={15} color={active.color} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold text-ink">{exp.name}</span>
                      <span className="block text-[11px] text-ink-mute">{exp.tags?.[0] ?? 'Activity'}</span>
                    </span>
                    <span className="text-right">
                      <span className="block text-[13px] font-bold text-ink">
                        {formatRupees(price)}
                      </span>
                      <span className="block text-[10px] font-bold text-[var(--color-teal)]">
                        +{Math.round(price / 100)} pts
                      </span>
                    </span>
                  </button>
                );
              })
            )}
          </div>

          <Button onClick={() => navigate('/search')} className="mt-1 w-full justify-center">
            See all in {active.name}
            <ArrowRight size={14} />
          </Button>
        </aside>
      </GlassCard>
    </section>
  );
}
